import { useEffect, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
  Heading1, Heading2, Heading3, Heading4, Heading5, Heading6,
  List, ListOrdered, Quote, Link2, Link2Off, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Undo, Redo, Minus,
} from "lucide-react";
import api, { resolveMediaUrl } from "@/lib/api";
import { toast } from "sonner";

const HEADINGS = [
  { level: 1, Icon: Heading1 },
  { level: 2, Icon: Heading2 },
  { level: 3, Icon: Heading3 },
  { level: 4, Icon: Heading4 },
  { level: 5, Icon: Heading5 },
  { level: 6, Icon: Heading6 },
];

export default function RichEditor({ value = "", onChange, placeholder = "Start writing…" }) {
  const fileRef = useRef();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        link: false,
        underline: false,
      }),
      Underline,
      Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: "rounded-xl my-4 max-w-full" } }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: "text-neon-cyan underline underline-offset-4 hover:text-neon-magenta transition-colors" },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
      Typography,
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none min-h-[420px] focus:outline-none",
        "data-testid": "rich-editor-content",
      },
    },
  });

  // Keep editor in sync when value is loaded async (initial fetch of blog)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  const insertLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Enter URL (or internal slug like /blog/my-post)", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const uploadImage = useCallback(async (file) => {
    if (!file || !editor) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const src = resolveMediaUrl(data.url);
      editor.chain().focus().setImage({ src, alt: file.name }).run();
      toast.success("Image inserted");
    } catch (e) {
      toast.error("Image upload failed");
    }
  }, [editor]);

  if (!editor) return null;

  const Btn = ({ onClick, active, disabled, label, testid, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-testid={testid}
      className={`w-8 h-8 rounded-md flex items-center justify-center transition ${
        active
          ? "bg-white text-bg-0"
          : "text-white/70 hover:text-white hover:bg-white/10"
      } disabled:opacity-40 disabled:hover:bg-transparent`}
    >
      {children}
    </button>
  );

  return (
    <div className="rounded-2xl border border-white/[0.08] bg-bg-1/40 overflow-hidden" data-testid="rich-editor">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 border-b border-white/[0.08] bg-bg-1/85 backdrop-blur px-2 py-2 flex flex-wrap items-center gap-1">
        <div className="flex items-center gap-0.5 pr-1 border-r border-white/10">
          <Btn label="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><Undo size={15} /></Btn>
          <Btn label="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><Redo size={15} /></Btn>
        </div>

        <div className="flex items-center gap-0.5 px-1 border-r border-white/10">
          {HEADINGS.map(({ level, Icon }) => (
            <Btn
              key={level}
              label={`H${level}`}
              testid={`fmt-h${level}`}
              active={editor.isActive("heading", { level })}
              onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
            >
              <Icon size={15} />
            </Btn>
          ))}
          <Btn
            label="Paragraph"
            active={editor.isActive("paragraph")}
            onClick={() => editor.chain().focus().setParagraph().run()}
          >
            <span className="font-mono text-[11px] font-bold">P</span>
          </Btn>
        </div>

        <div className="flex items-center gap-0.5 px-1 border-r border-white/10">
          <Btn label="Bold" testid="fmt-bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={14} /></Btn>
          <Btn label="Italic" testid="fmt-italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={14} /></Btn>
          <Btn label="Underline" testid="fmt-underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={14} /></Btn>
          <Btn label="Strike" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={14} /></Btn>
          <Btn label="Inline code" active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}><Code size={14} /></Btn>
        </div>

        <div className="flex items-center gap-0.5 px-1 border-r border-white/10">
          <Btn label="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={15} /></Btn>
          <Btn label="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15} /></Btn>
          <Btn label="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={14} /></Btn>
          <Btn label="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={15} /></Btn>
        </div>

        <div className="flex items-center gap-0.5 px-1 border-r border-white/10">
          <Btn label="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft size={14} /></Btn>
          <Btn label="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter size={14} /></Btn>
          <Btn label="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight size={14} /></Btn>
        </div>

        <div className="flex items-center gap-0.5 px-1">
          <Btn label="Add link" testid="fmt-link" active={editor.isActive("link")} onClick={insertLink}><Link2 size={14} /></Btn>
          <Btn label="Remove link" onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive("link")}><Link2Off size={14} /></Btn>
          <Btn label="Insert image" testid="fmt-image" onClick={() => fileRef.current?.click()}><ImageIcon size={14} /></Btn>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) uploadImage(f);
            }}
          />
        </div>
      </div>

      <div className="px-6 py-8 max-h-[70vh] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
